import React, { useCallback, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { EIntervalsRepeat } from 'constants/enums';
import { FormikErrors, FormikTouched } from 'formik';
import { permissionsUtil } from 'utils/permissionsUtil';
import { dayInMonthOptions, intervalsRepeatOptions, roundedHoursOptions } from 'utils/selectOptionsUtils';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';

interface IntervalFormValues {
    playerAvailability?: number;
    setAsFree?: boolean;
    [key: string]: unknown;
}

interface IntervalProps {
    repeat: EIntervalsRepeat;
    setRepeat: (repeat: EIntervalsRepeat) => void;
    startHour: string;
    setStartHour: (hour: string) => void;
    weekDay: number;
    setWeekDay: (day: number) => void;
    dayInMonth: number;
    setDayInMonth: (day: number) => void;
    values: IntervalFormValues;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    touched: FormikTouched<IntervalFormValues>;
    errors: FormikErrors<IntervalFormValues>;
    setFieldValue: (field: string, value: unknown) => void;
}

const WEEK_DAYS_OPTIONS = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 }
];

const Interval: React.FC<IntervalProps> = ({
    repeat,
    setRepeat,
    startHour,
    setStartHour,
    weekDay,
    setWeekDay,
    dayInMonth,
    setDayInMonth,
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue
}) => {
    const { publisherId } = useParams();
    const isSuperAdmin = publisherId && permissionsUtil.isSuperAdminByProject(publisherId);

    const handleRepeatChange = useCallback(
        (value: string) => setRepeat(value as EIntervalsRepeat),
        [setRepeat]
    );

    const handleStartHourChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setStartHour(e.target.value);
            setFieldValue('startHour', e.target.value);
        },
        [setStartHour, setFieldValue]
    );

    const handleStartHourSelectChange = useCallback(
        (value: string) => {
            setStartHour(value);
            setFieldValue('startHour', value);
        },
        [setStartHour, setFieldValue]
    );

    const handleWeekDayChange = useCallback(
        (value: string) => setWeekDay(Number(value)),
        [setWeekDay]
    );

    const handleDayInMonthChange = useCallback(
        (value: string) => setDayInMonth(Number(value)),
        [setDayInMonth]
    );

    const selectedRepeatOption = useMemo(
        () => intervalsRepeatOptions.find((option) => option.value === repeat),
        [repeat]
    );

    const selectedStartHourOption = useMemo(
        () => roundedHoursOptions.find((option) => option.value === startHour),
        [startHour]
    );

    const selectedWeekDayOption = useMemo(
        () => WEEK_DAYS_OPTIONS.find((option) => option.value === weekDay),
        [weekDay]
    );

    const selectedDayInMonthOption = useMemo(
        () => dayInMonthOptions.find((option) => option.value === dayInMonth),
        [dayInMonth]
    );

    const startHourError = useMemo(
        () => touched?.startHour && errors?.startHour ? 'error' : 'default',
        [touched?.startHour, errors?.startHour]
    );

    const startHourDescription = useMemo(
        () => (touched?.startHour && errors?.startHour ? String(errors.startHour) : ''),
        [touched?.startHour, errors?.startHour]
    );

    return (
        <div className="flex flex-col gap-6 w-full">
            <div className="flex gap-5">
                <Input
                    label="Availability"
                    id="availability-intervals"
                    name="playerAvailability"
                    type="number"
                    min={0}
                    placeholder="Set availability"
                    value={values.playerAvailability || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isRequired={values.setAsFree}
                    state={
                        touched?.playerAvailability && Boolean(errors?.playerAvailability)
                            ? 'error'
                            : 'default'
                    }
                    description={
                        touched?.playerAvailability
                            ? errors?.playerAvailability?.toString()
                            : ''
                    }
                />
                <div className="w-full">
                    <InputSelectDropdown
                        label="Repeat"
                        listDataTestId="intervalRepeatList"
                        buttonDataTestId="intervalRepeatButton"
                        options={intervalsRepeatOptions}
                        defaultSelected={selectedRepeatOption}
                        onOptionChange={handleRepeatChange}
                    />
                </div>
            </div>
            <div className="flex gap-5">
                {repeat !== EIntervalsRepeat.NONE && (
                    <div className="space-y-2">
                        {isSuperAdmin ? (
                            <Input
                                label="Hour (UTC)"
                                isRequired
                                name="startHour"
                                type="time"
                                className="w-[100px]"
                                value={startHour}
                                onChange={handleStartHourChange}
                                onBlur={handleBlur}
                                state={startHourError}
                                description={startHourDescription}
                            />
                        ) : (
                            <>
                                <div className="w-[100px]">
                                    <InputSelectDropdown
                                        label="* Hour (UTC)"
                                        options={roundedHoursOptions}
                                        defaultSelected={selectedStartHourOption}
                                        onOptionChange={handleStartHourSelectChange}
                                    />
                                </div>
                                {touched?.startHour && errors?.startHour && (
                                    <p className="text-sm text-red-500">{startHourDescription}</p>
                                )}
                            </>
                        )}
                    </div>
                )}

                {repeat === EIntervalsRepeat.WEEKLY && (
                    <div className="w-[160px]">
                        <InputSelectDropdown
                            label="Week day"
                            options={WEEK_DAYS_OPTIONS}
                            defaultSelected={selectedWeekDayOption}
                            onOptionChange={handleWeekDayChange}
                        />
                    </div>
                )}

                {repeat === EIntervalsRepeat.MONTHLY && (
                    <div className="w-[160px]">
                        <InputSelectDropdown
                            label="Month day"
                            options={dayInMonthOptions}
                            defaultSelected={selectedDayInMonthOption}
                            onOptionChange={handleDayInMonthChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Interval;

